import { createContext, useContext, useState, type ReactNode } from 'react';

export type ConsentChoice = 'accepted' | 'rejected';

const STORAGE_KEY = 'cookie-consent';

interface ConsentContextType {
  choice: ConsentChoice | null;
  accept: () => void;
  reject: () => void;
  reopen: () => void;
}

const ConsentContext = createContext<ConsentContextType | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<ConsentChoice | null>(() =>
    typeof window === 'undefined' ? null : (localStorage.getItem(STORAGE_KEY) as ConsentChoice | null)
  );

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setChoice('accepted');
  };

  const reject = () => {
    localStorage.setItem(STORAGE_KEY, 'rejected');
    setChoice('rejected');
  };

  const reopen = () => {
    localStorage.removeItem(STORAGE_KEY);
    setChoice(null);
  };

  return (
    <ConsentContext.Provider value={{ choice, accept, reject, reopen }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
  return ctx;
}
